'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useSubscribedState } from '@/hooks/useSubscribedState';
import { parseEnumValue, parseStringList, toggleArrayItem } from '../utils';
export type PolicySortOption = 'SCORE' | 'LATEST';
export interface PolicyFilterState {
  keyword: string;
  targetCodes: string[];
  provisionCodes: string[];
  jobCodes: string[];
  ageCodes: string[];
  sort: PolicySortOption;
  page: number;
}
const SORT_OPTIONS: readonly PolicySortOption[] = ['SCORE', 'LATEST'];
const DEFAULTS: PolicyFilterState = {
  keyword: '',
  targetCodes: [],
  provisionCodes: [],
  jobCodes: [],
  ageCodes: [],
  sort: 'SCORE',
  page: 1
};
const PARAM_KEYS = {
  keyword: 'q',
  targetCodes: 'targetCodes',
  provisionCodes: 'provisionCodes',
  jobCodes: 'jobCodes',
  ageCodes: 'ageCodes',
  sort: 'sort',
  page: 'page'
} as const;
function parsePage(value: string | null): number {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue) || parsedValue < 1) return DEFAULTS.page;
  return Math.floor(parsedValue);
}
function parse(params: URLSearchParams): PolicyFilterState {
  return {
    keyword: params.get(PARAM_KEYS.keyword) ?? DEFAULTS.keyword,
    targetCodes: parseStringList(params.get(PARAM_KEYS.targetCodes)),
    provisionCodes: parseStringList(params.get(PARAM_KEYS.provisionCodes)),
    jobCodes: parseStringList(params.get(PARAM_KEYS.jobCodes)),
    ageCodes: parseStringList(params.get(PARAM_KEYS.ageCodes)),
    sort: parseEnumValue(params.get(PARAM_KEYS.sort), SORT_OPTIONS, DEFAULTS.sort),
    page: parsePage(params.get(PARAM_KEYS.page))
  };
}
function setOrDelete(params: URLSearchParams, key: string, value: string | undefined) {
  if (value) params.set(key, value);else params.delete(key);
}
function write(params: URLSearchParams, state: PolicyFilterState) {
  setOrDelete(params, PARAM_KEYS.keyword, state.keyword);
  setOrDelete(params, PARAM_KEYS.targetCodes, state.targetCodes.length ? state.targetCodes.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.provisionCodes, state.provisionCodes.length ? state.provisionCodes.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.jobCodes, state.jobCodes.length ? state.jobCodes.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.ageCodes, state.ageCodes.length ? state.ageCodes.join(',') : undefined);
  setOrDelete(params, PARAM_KEYS.sort, state.sort !== DEFAULTS.sort ? state.sort : undefined);
  setOrDelete(params, PARAM_KEYS.page, state.page !== DEFAULTS.page ? String(state.page) : undefined);
}
type NavigateMethod = 'push' | 'replace';
export function usePolicyFilter() {
  const $ = _c(46);
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.toString() ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const paramsString = t0;
  let t1;
  let t2;
  if ($[2] !== paramsString) {
    t1 = () => parse(new URLSearchParams(paramsString));
    t2 = [paramsString];
    $[2] = paramsString;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  const [state, setState] = useSubscribedState(t1, t2);
  const pendingRef = useRef(null);
  let t3;
  if ($[5] !== paramsString || $[6] !== pathname || $[7] !== router) {
    t3 = () => {
      const pending = pendingRef.current;
      if (!pending) {
        return;
      }
      pendingRef.current = null;
      const params = new URLSearchParams(paramsString);
      write(params, pending.state);
      const qs = params.toString();
      const url = qs ? `${pathname}?${qs}` : pathname;
      if (pending.method === "push") {
        router.push(url, {
          scroll: false
        });
      } else {
        router.replace(url, {
          scroll: false
        });
      }
    };
    $[5] = paramsString;
    $[6] = pathname;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== paramsString || $[10] !== pathname || $[11] !== router || $[12] !== state) {
    t4 = [state, pathname, paramsString, router];
    $[9] = paramsString;
    $[10] = pathname;
    $[11] = router;
    $[12] = state;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== setState) {
    t5 = (updater, t6) => {
      const method = t6 === undefined ? "replace" : t6;
      setState(prev => {
        const next = updater(prev);
        pendingRef.current = {
          state: next,
          method
        };
        return next;
      });
    };
    $[14] = setState;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const update = t5;
  let t6;
  if ($[16] !== update) {
    t6 = keyword => {
      update(prev_0 => ({
        ...prev_0,
        keyword,
        page: DEFAULTS.page
      }), "push");
      window.scrollTo(0, 0);
    };
    $[16] = update;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const setKeyword = t6;
  let t7;
  if ($[18] !== update) {
    t7 = () => {
      update(_temp);
    };
    $[18] = update;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const clearKeyword = t7;
  let t8;
  if ($[20] !== update) {
    t8 = code => {
      update(prev_2 => ({
        ...prev_2,
        targetCodes: toggleArrayItem(prev_2.targetCodes, code),
        page: DEFAULTS.page
      }));
    };
    $[20] = update;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const toggleTargetCode = t8;
  let t9;
  if ($[22] !== update) {
    t9 = code_0 => {
      update(prev_3 => ({
        ...prev_3,
        provisionCodes: toggleArrayItem(prev_3.provisionCodes, code_0),
        page: DEFAULTS.page
      }));
    };
    $[22] = update;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const toggleProvisionCode = t9;
  let t10;
  if ($[24] !== update) {
    t10 = code_1 => {
      update(prev_4 => ({
        ...prev_4,
        jobCodes: toggleArrayItem(prev_4.jobCodes, code_1),
        page: DEFAULTS.page
      }));
    };
    $[24] = update;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const toggleJobCode = t10;
  let t11;
  if ($[26] !== update) {
    t11 = code_2 => {
      update(prev_5 => ({
        ...prev_5,
        ageCodes: toggleArrayItem(prev_5.ageCodes, code_2),
        page: DEFAULTS.page
      }));
    };
    $[26] = update;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const toggleAgeCode = t11;
  let t12;
  if ($[28] !== update) {
    t12 = () => {
      update(_temp2);
    };
    $[28] = update;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const resetFilters = t12;
  let t13;
  if ($[30] !== update) {
    t13 = sort => {
      update(prev_7 => ({
        ...prev_7,
        sort,
        page: DEFAULTS.page
      }));
    };
    $[30] = update;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const setSort = t13;
  let t14;
  if ($[32] !== update) {
    t14 = page => {
      update(prev_8 => ({
        ...prev_8,
        page
      }));
    };
    $[32] = update;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const setPage = t14;
  const activeFilterCount = state.targetCodes.length + state.provisionCodes.length + state.jobCodes.length + state.ageCodes.length;
  let t15;
  if ($[34] !== activeFilterCount || $[35] !== clearKeyword || $[36] !== resetFilters || $[37] !== setKeyword || $[38] !== setPage || $[39] !== setSort || $[40] !== state || $[41] !== toggleAgeCode || $[42] !== toggleJobCode || $[43] !== toggleProvisionCode || $[44] !== toggleTargetCode) {
    t15 = {
      state,
      setKeyword,
      clearKeyword,
      toggleTargetCode,
      toggleProvisionCode,
      toggleJobCode,
      toggleAgeCode,
      resetFilters,
      setSort,
      setPage,
      activeFilterCount
    };
    $[34] = activeFilterCount;
    $[35] = clearKeyword;
    $[36] = resetFilters;
    $[37] = setKeyword;
    $[38] = setPage;
    $[39] = setSort;
    $[40] = state;
    $[41] = toggleAgeCode;
    $[42] = toggleJobCode;
    $[43] = toggleProvisionCode;
    $[44] = toggleTargetCode;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
function _temp2(prev_6) {
  return {
    ...prev_6,
    targetCodes: [],
    provisionCodes: [],
    jobCodes: [],
    ageCodes: [],
    page: DEFAULTS.page
  };
}
function _temp(prev_1) {
  return {
    ...prev_1,
    keyword: ""
  };
}